import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useNotifications } from "../../../contexts/notification";
import OnlineStatusIndicator from "./OnlineStatusIndicator";
import { handleEndCallProStudent } from "./handleEndCall";
import {
  FaVideo,
  FaVideoSlash,
  FaMicrophone,
  FaMicrophoneSlash,
  FaDesktop,
  FaPhoneSlash,
  FaUser,
} from "react-icons/fa";

// Styled components for the video call interface
const VideoCallContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  /* Set the container to have no background and no pointer events when screen sharing */
  background-color: ${(props) =>
    props.isScreenSharing ? "transparent" : "rgba(0, 0, 0, 0.9)"};
  pointer-events: ${(props) => (props.isScreenSharing ? "none" : "auto")};

  /* These elements should always receive events even during screen sharing */
  ${(props) =>
    props.isScreenSharing &&
    `
    & > * {
      pointer-events: auto;
    }
  `}
`;

const CallHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: rgba(33, 33, 33, 0.9);
  color: white;
  pointer-events: auto; /* This should always receive clicks */

  h3 {
    margin: 0;
    flex: 1;
    text-align: left;
  }
`;

const CallContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 20px;
  pointer-events: none; /* Make this transparent to clicks by default */
`;

const ParticipantVideo = styled.div`
  width: 80%;
  height: 70%;
  max-height: calc(100vh - 240px); /* Increased space for bottom controls */
  background-color: #1a1a1a;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
  pointer-events: auto; /* This should receive clicks */

  ${(props) =>
    props.isScreenSharing &&
    `
    width: 200px;
    height: 150px;
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 5;
    border: 2px solid #2196f3;
  `}

  img {
    width: 100%;
    height: 100%;
    object-fit: contain; /* Changed from cover to contain */
    max-height: 100%;
  }

  .video-off {
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;

    svg {
      font-size: 3rem;
      margin-bottom: 1rem;
    }
  }
`;

const UserVideo = styled.div`
  position: absolute;
  width: 200px;
  height: 150px;
  bottom: 120px; /* Moved up to avoid colliding with bottom UI bar */
  right: 20px;
  background-color: #333;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #2196f3;
  z-index: 5;
  pointer-events: auto; /* Ensure this always receives clicks */

  /* Remove the special screen sharing positioning */

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-off {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;

    svg {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      font-size: 0.75rem;
    }
  }
`;

const ScreenShareContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 20px;
  background-color: rgba(33, 150, 243, 0.6);
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  pointer-events: none; /* This ensures clicks pass through */

  svg {
    font-size: 1.2rem;
    margin-right: 8px;
    color: white;
  }
`;

const CallControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background-color: rgba(33, 33, 33, 0.9);
  z-index: 10;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
  pointer-events: auto; /* Ensure controls always receive clicks */
`;

const ControlButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props) =>
    props.danger ? "#f44336" : props.active ? "#2196f3" : "#424242"};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.danger ? "#d32f2f" : props.active ? "#1976d2" : "#616161"};
  }

  svg {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 0.75rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f44336;
  }
`;

const CallNotification = styled.div`
  position: absolute;
  top: 5rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(33, 150, 243, 0.9);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  animation: fadeIn 0.3s, fadeOut 0.3s 3s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -20px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -20px);
    }
  }
`;

const VideoCallInterface = ({ participant, onEndCall }) => {
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callNotification, setCallNotification] = useState(null);
  const { addNotification } = useNotifications();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const showCallNotification = useCallback((message) => {
    setCallNotification(message);
    setTimeout(() => {
      setCallNotification(null);
    }, 3000);
  }, []);
  const handleEndCall = useCallback(() => {
    console.log("Manual handleEndCall triggered");
    handleEndCallProStudent(
      streamRef,
      onEndCall,
      participant,
      addNotification,
      false
    );
  }, [onEndCall, participant, addNotification]);

  const handleParticipantLeft = useCallback(() => {
    // Show in-call notification
    showCallNotification(`${participant?.name || "User"} left the call`);

    // Add system notification
    addNotification({
      title: "Participant Left",
      message: `${participant?.name || "User"} has left the call`,
      type: "info",
      forRole: "proStudent",
    });
  }, [participant, addNotification, showCallNotification]);

  useEffect(() => {
    // Handle clicks outside the video call interface
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleEndCall();
      }
    };

    // Add event listener only to the specific elements that should close the call
    const dashboardNav = document.querySelector("nav");
    if (dashboardNav) {
      dashboardNav.addEventListener("click", handleClickOutside);
    }

    return () => {
      if (dashboardNav) {
        dashboardNav.removeEventListener("click", handleClickOutside);
      }
    };
  }, [handleEndCall]);

  // Handle starting and stopping the camera
  useEffect(() => {
    if (isVideoEnabled) {
      // Request access to the webcam
      navigator.mediaDevices
        .getUserMedia({ video: true, audio: isAudioEnabled })
        .then((stream) => {
          streamRef.current = stream;

          // Display the video stream if videoRef is available
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((error) => {
          console.error("Error accessing camera:", error);
          showCallNotification("Could not access camera");
          setIsVideoEnabled(false);
        });
    } else {
      // Stop the stream if it exists
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach((track) => track.stop());
        streamRef.current = null;

        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    }

    // Cleanup function
    return () => {
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [isVideoEnabled, isAudioEnabled, showCallNotification]);

  // Show notification when user joins the call
  useEffect(() => {
    showCallNotification(`${participant?.name || "User"} joined the call`);
  }, [participant, showCallNotification]);

  // Simulate the other participant leaving after some time
  useEffect(() => {
    if (!participant) return;

    // Random time between 1-3 minutes for demo purpose
    const randomTime = Math.floor(Math.random() * (180000 - 60000) + 60000);

    const timer = setTimeout(() => {
      handleParticipantLeft();
    }, randomTime);

    return () => clearTimeout(timer);
  }, [participant, handleParticipantLeft]);

  const toggleVideo = () => {
    setIsVideoEnabled((prev) => !prev);
    showCallNotification(`Camera ${!isVideoEnabled ? "enabled" : "disabled"}`);
  };

  const toggleAudio = () => {
    setIsAudioEnabled((prev) => !prev);

    // If we have an active stream, toggle the audio tracks
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !isAudioEnabled;
      });
    }

    showCallNotification(`Microphone ${!isAudioEnabled ? "unmuted" : "muted"}`);
  };

  const toggleScreenShare = () => {
    setIsScreenSharing((prev) => !prev);
    showCallNotification(
      `Screen sharing ${!isScreenSharing ? "started" : "stopped"}`
    );
  };

  return (
    <VideoCallContainer ref={containerRef} isScreenSharing={isScreenSharing}>
      <CallHeader>
        <h3>Call with {participant?.name || "User"}</h3>
        <OnlineStatusIndicator isOnline={true} />
        <CloseButton onClick={handleEndCall}>
          <FaUser />
        </CloseButton>
      </CallHeader>

      <CallContent>
        {isScreenSharing ? (
          <>
            <ScreenShareContainer>
              <FaDesktop />
              <span>Screen sharing is active</span>
            </ScreenShareContainer>

            <ParticipantVideo isScreenSharing={true}>
              {participant ? (
                <img
                  src={
                    participant.avatar ||
                    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80"
                  }
                  alt={participant.name}
                />
              ) : (
                <div className="video-off">
                  <FaVideoSlash />
                  <p>Participant camera is off</p>
                </div>
              )}
            </ParticipantVideo>
          </>
        ) : (
          <ParticipantVideo>
            {participant ? (
              <img
                src={
                  participant.avatar ||
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80"
                }
                alt={participant.name}
              />
            ) : (
              <div className="video-off">
                <FaVideoSlash />
                <p>Participant camera is off</p>
              </div>
            )}
          </ParticipantVideo>
        )}
        <UserVideo>
          {isVideoEnabled ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ 
                width: "100%", 
                height: "100%", 
                objectFit: "cover",
                transform: "scaleX(-1)" /* Mirror the video horizontally */
              }}
            />
          ) : (
            <div className="video-off">
              <FaVideoSlash />
              <p>Your camera is off</p>
            </div>
          )}
        </UserVideo>

        {callNotification && (
          <CallNotification>{callNotification}</CallNotification>
        )}
      </CallContent>

      <CallControls>
        <ControlButton onClick={toggleVideo} active={isVideoEnabled}>
          {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
          <span>{isVideoEnabled ? "Disable Video" : "Enable Video"}</span>
        </ControlButton>

        <ControlButton onClick={toggleAudio} active={isAudioEnabled}>
          {isAudioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
          <span>{isAudioEnabled ? "Mute" : "Unmute"}</span>
        </ControlButton>

        <ControlButton onClick={toggleScreenShare} active={isScreenSharing}>
          <FaDesktop />
          <span>{isScreenSharing ? "Stop Sharing" : "Share Screen"}</span>
        </ControlButton>

        <ControlButton onClick={handleEndCall} danger>
          <FaPhoneSlash />
          <span>End Call</span>
        </ControlButton>
      </CallControls>
    </VideoCallContainer>
  );
};

export default VideoCallInterface;
